import type { SiteExportData } from "./data-fetcher";
import { formatReseau } from "@/modules/corrosion/types";
import { EXCEL_CORROSION_FILLS as CORROSION_FILLS } from "../palette";

/** Formule Excel du % corrosion : (épaisseur d'origine − épaisseur min) / épaisseur d'origine.
 *  Centralisée pour éviter les erreurs de colonne lors d'évolutions du layout (M9). */
function corrosionFormula(epaisseurCol: string, minCol: string, row: number): string {
  return `=(${epaisseurCol}${row}-${minCol}${row})/${epaisseurCol}${row}`;
}

/** En-têtes des 10 colonnes (identiques au format Renaud) */
const HEADERS = [
  "US N°",
  "Position",
  "Localisation",
  "Diamètre Exterieur",
  "DN",
  "Epaisseur d'origine en mm",
  "Epaisseur maxi en mm",
  "Epaisseur Mini en mm",
  "Matériau",
  "% Corrosion",
];

/** Largeurs de colonnes reproduites depuis le fichier modèle */
const COL_WIDTHS = [14, 14, 40, 13, 11, 18, 16, 16, 20, 11];

/**
 * Génère un fichier Excel (.xlsx) au format de Renaud.
 * Une feuille par zone, 10 colonnes, formule % corrosion avec couleur de fond.
 */
export async function generateExcelBlob(
  data: SiteExportData
): Promise<Blob> {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();

  for (const zoneData of data.zones) {
    // Nom de feuille tronqué à 31 caractères (limite Excel)
    const sheetName = zoneData.zone.name.slice(0, 31);
    const sheet = workbook.addWorksheet(sheetName);

    // Largeurs de colonnes
    sheet.columns = COL_WIDTHS.map((width, i) => ({
      key: `col${i}`,
      width,
    }));

    // ─── En-tête (row 1) ───
    const headerRow = sheet.addRow(HEADERS);
    headerRow.height = 20;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" },
      };
      cell.border = thinBorder();
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    });

    // ─── Données (une ligne par mesure) ───
    // Modèle B : zone → pins → mesure (pour la campagne exportée)
    for (const pinData of zoneData.pins) {
      const m = pinData.mesure;
      if (!m) continue;

        const rowNum = sheet.rowCount + 1;
        const row = sheet.addRow([
          m.numero,
          m.orientation === "V" ? "Verticale" : "Horizontale",
          formatReseau(m),
          m.diametreExt ?? "-",
          m.dn ?? "-",
          m.epaisseurOrigine ?? "-",
          m.mesureMax ?? "-",
          m.mesureMin ?? "-",
          m.materiau || "-",
          null, // Colonne J = formule
        ]);

        row.height = 17;

        // Formule % Corrosion en colonne J : =(F-H)/F
        const jCell = row.getCell(10);
        const hasValues =
          typeof m.epaisseurOrigine === "number" &&
          typeof m.mesureMin === "number";

        if (hasValues) {
          jCell.value = {
            formula: corrosionFormula("F", "H", rowNum),
            result: m.pourcentageCorrosion
              ? m.pourcentageCorrosion / 100
              : undefined,
          } as import("exceljs").CellFormulaValue;
        } else {
          jCell.value = "-";
        }

        // Fond coloré selon le code couleur
        const fillColor =
          m.codeCouleur && CORROSION_FILLS[m.codeCouleur]
            ? CORROSION_FILLS[m.codeCouleur]
            : CORROSION_FILLS.green;

        if (hasValues && m.codeCouleur) {
          jCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: fillColor },
          };
        }

        // Formatage de chaque cellule
        row.eachCell((cell, colNumber) => {
          cell.border = thinBorder();

          // Alignement
          if (colNumber === 2) {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          } else if (colNumber === 3) {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          } else {
            cell.alignment = { horizontal: "center", vertical: "middle" };
          }

          // Format numérique
          if (colNumber === 1) cell.numFmt = "0";
          if (colNumber === 4) cell.numFmt = "0.0";
          if (colNumber === 5) cell.numFmt = "0"; // DN : entier
          if (colNumber >= 6 && colNumber <= 8) cell.numFmt = "0.00";
          if (colNumber === 10) {
            cell.numFmt = "0.0%";
            cell.font = { bold: true };
          }
        });
    }
  }

  // Générer le buffer et créer le Blob
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** Bordures fines sur les 4 côtés */
function thinBorder(): Partial<import("exceljs").Borders> {
  const side = { style: "thin" as const };
  return { top: side, bottom: side, left: side, right: side };
}

// ─────────────────────────────────────────────────────────────
// Export COMPARATIF : campagne N vs campagne N-X
// ─────────────────────────────────────────────────────────────

const COMPARE_FILLS = {
  better:  "FF92D050", // vert : corrosion diminuee
  worse:   "FFFF0000", // rouge : corrosion augmentee
  neutral: "FFD9D9D9", // gris : stable ou pas de delta
};

/**
 * Génère un Excel comparatif campagne N vs campagne N-X.
 *
 * Structure : une feuille par zone, 2 lignes d'en-tête fusionnées
 * (colonnes campagne N et colonnes campagne N-X), puis colonne Evolution.
 *
 * dataN  : campagne de reference "actuelle"
 * dataNx : campagne ancienne de comparaison
 */
export async function generateComparativeExcelBlob(
  dataN: SiteExportData,
  dataNx: SiteExportData
): Promise<Blob> {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();

  const labelN  = formatCampagneDate(dataN.campagne.date);
  const labelNx = formatCampagneDate(dataNx.campagne.date);

  // Index des mesures N-X par pinId pour jointure rapide
  const mesureNxByPin = new Map<string, typeof dataNx.zones[number]["pins"][number]["mesure"]>();
  for (const zoneData of dataNx.zones) {
    for (const pinData of zoneData.pins) {
      if (pinData.mesure) mesureNxByPin.set(pinData.pin.id, pinData.mesure);
    }
  }

  for (const zoneData of dataN.zones) {
    const sheetName = zoneData.zone.name.slice(0, 31);
    const sheet = workbook.addWorksheet(sheetName);

    // Colonnes : A-F communes + G-I campagne N + J-L campagne N-X + M Evolution
    const colWidths = [8, 14, 35, 13, 11, 18, 16, 16, 14, 16, 16, 14, 14];
    sheet.columns = colWidths.map((width) => ({ width }));

    // ─── Ligne 1 : en-têtes fusionnées de groupes ───
    const row1 = sheet.addRow([
      "", "", "", "", "", "",
      `Campagne ${labelN}`, "", "",
      `Campagne ${labelNx}`, "", "",
      "Evolution",
    ]);
    row1.height = 22;
    // Fusionner G1:I1
    sheet.mergeCells(1, 7, 1, 9);
    sheet.mergeCells(1, 10, 1, 12);
    // Style de groupe
    styleGroupHeader(row1.getCell(7), "FF4472C4"); // bleu campagne N
    styleGroupHeader(row1.getCell(10), "FF70AD47"); // vert campagne N-X
    styleGroupHeader(row1.getCell(13), "FF7030A0"); // violet evolution
    // Cellules communes vides
    for (let c = 1; c <= 6; c++) {
      const cell = row1.getCell(c);
      cell.border = thinBorder();
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9D9D9" } };
    }

    // ─── Ligne 2 : en-têtes colonnes ───
    const row2 = sheet.addRow([
      "US N°", "Position", "Localisation",
      "Diam. Ext.", "DN", "Ep. Origine (mm)",
      "Ep. Max", "Ep. Min", "% Corrosion",
      "Ep. Max", "Ep. Min", "% Corrosion",
      "+/- points %",
    ]);
    row2.height = 20;
    row2.eachCell((cell) => {
      cell.font = { bold: true, size: 9 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9D9D9" } };
      cell.border = thinBorder();
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    });

    // ─── Données ───
    for (const pinData of zoneData.pins) {
      const mN = pinData.mesure;
      if (!mN) continue;

      const mNx = mesureNxByPin.get(pinData.pin.id) ?? null;
      const rowNum = sheet.rowCount + 1;

      const row = sheet.addRow([
        mN.numero,
        mN.orientation === "V" ? "Verticale" : "Horizontale",
        formatReseau(mN),
        mN.diametreExt ?? "-",
        mN.dn ?? "-",
        mN.epaisseurOrigine ?? "-",
        // Campagne N
        mN.mesureMax ?? "-",
        mN.mesureMin ?? "-",
        null, // formule % corrosion N
        // Campagne N-X
        mNx?.mesureMax ?? "-",
        mNx?.mesureMin ?? "-",
        null, // formule % corrosion Nx
        // Evolution
        null, // formule evolution
      ]);
      row.height = 17;

      // % Corrosion N : formule =(F-H)/F
      const hasN = typeof mN.epaisseurOrigine === "number" && typeof mN.mesureMin === "number";
      const cellI = row.getCell(9);
      if (hasN) {
        cellI.value = {
          formula: corrosionFormula("F", "H", rowNum),
          result: mN.pourcentageCorrosion ? mN.pourcentageCorrosion / 100 : undefined,
        } as import("exceljs").CellFormulaValue;
        cellI.numFmt = "0.0%";
        if (mN.codeCouleur && CORROSION_FILLS[mN.codeCouleur]) {
          cellI.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORROSION_FILLS[mN.codeCouleur] } };
        }
      } else {
        cellI.value = "-";
      }

      // % Corrosion Nx
      const hasNx = mNx && typeof mNx.epaisseurOrigine === "number" && typeof mNx.mesureMin === "number";
      const cellL = row.getCell(12);
      if (hasNx) {
        const jCol = rowNum; // ligne courante
        cellL.value = {
          formula: corrosionFormula("F", "K", jCol),
          result: mNx.pourcentageCorrosion ? mNx.pourcentageCorrosion / 100 : undefined,
        } as import("exceljs").CellFormulaValue;
        cellL.numFmt = "0.0%";
        if (mNx.codeCouleur && CORROSION_FILLS[mNx.codeCouleur]) {
          cellL.fill = { type: "pattern", pattern: "solid", fgColor: { argb: CORROSION_FILLS[mNx.codeCouleur] } };
        }
      } else {
        cellL.value = "-";
      }

      // Evolution : I - L (en points %, affiché comme différence)
      const cellM = row.getCell(13);
      const pctN  = mN.pourcentageCorrosion;
      const pctNx = mNx?.pourcentageCorrosion ?? null;
      if (pctN != null && pctNx != null) {
        const delta = pctN - pctNx;
        cellM.value = delta / 100;
        cellM.numFmt = '+0.0%;-0.0%;"="';
        cellM.font = { bold: true };
        if (delta > 2) {
          cellM.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COMPARE_FILLS.worse } };
        } else if (delta < -2) {
          cellM.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COMPARE_FILLS.better } };
        } else {
          cellM.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COMPARE_FILLS.neutral } };
        }
      } else {
        cellM.value = mNx ? "N/A" : "Nouveau";
        cellM.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COMPARE_FILLS.neutral } };
      }

      // Bordures et alignements
      row.eachCell((cell, col) => {
        cell.border = thinBorder();
        cell.alignment = {
          horizontal: col === 3 ? "left" : "center",
          vertical: "middle",
        };
        if (col === 1) cell.numFmt = "0";
        if (col === 4) cell.numFmt = "0.0";
        if (col === 5) cell.numFmt = "0";
        if (col === 6 || col === 7 || col === 8 || col === 10 || col === 11) {
          cell.numFmt = "0.00";
        }
      });
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

function formatCampagneDate(date: Date | string): string {
  return new Date(date).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function styleGroupHeader(cell: import("exceljs").Cell, argbColor: string) {
  cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: argbColor } };
  cell.border = thinBorder();
  cell.alignment = { horizontal: "center", vertical: "middle" };
}
